// File d'attente persistante des opérations hors connexion (architecture, section 8 ; cadrage, section 9).
// Chaque opération porte un identifiant client unique (idempotence côté serveur), l'horodatage de
// l'événement et celui de l'enregistrement local ; sa charge est chiffrée. Une empreinte de la file est
// conservée séparément pour détecter une purge du stockage par le système. Un second magasin conserve, chiffrées,
// les données locales (paquet de travail versionné). L'envoi vers /api/v1/sync est assuré par le synchroniseur.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Chiffre, CoffreLocal } from './chiffrement';

export const ETATS_OPERATION = ['enregistre_local', 'en_attente_envoi', 'envoye', 'synchronise', 'conflit', 'refuse'] as const;
export type EtatOperation = (typeof ETATS_OPERATION)[number];

export const LIBELLES_ETATS_OPERATION: Record<EtatOperation, string> = {
  enregistre_local: 'Enregistré sur cet appareil',
  en_attente_envoi: 'En attente d’envoi',
  envoye: 'Envoyé, en attente d’accusé',
  synchronise: 'Synchronisé',
  conflit: 'Conflit à résoudre',
  refuse: 'Refusé',
};

export interface OperationStockee {
  identifiantClient: string;
  type: string;
  charge: Chiffre;
  horodatageEvenement: string;
  horodatageLocal: string;
  etat: EtatOperation;
  motif: string | null;
  /** Version de l'objet connue de l'appareil au moment de la saisie (détection de conflit par le serveur). */
  versionObjet?: string | null;
  /** Libellé lisible de l'opération (« Déclaration de stock, Station X »), chiffré avec la charge. */
  resume?: Chiffre | null;
  code?: string | null;
  conflitId?: string | null;
  envoyeLe?: string | null;
  accuseLe?: string | null;
  /** Réponse du serveur (identifiants créés, état de l'objet), conservée pour l'affichage. */
  resultat?: unknown;
}

export interface DonneeLocale {
  cle: string;
  version: string | null;
  enregistreLe: string;
  contenu: Chiffre;
}

interface SchemaHorsLigne extends DBSchema {
  operations: { key: string; value: OperationStockee; indexes: { etat: string } };
  donnees: { key: string; value: DonneeLocale };
}

export interface StockageEmpreinte {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
}

const CLE_EMPREINTE = 'ct-empreinte-file';

export interface OptionsAjout {
  horodatageEvenement?: Date;
  versionObjet?: string | null;
  resume?: string | null;
}

export class FileHorsLigne {
  private base: Promise<IDBPDatabase<SchemaHorsLigne>>;

  constructor(
    private readonly coffre: CoffreLocal,
    private readonly empreintes: StockageEmpreinte,
    nomBase = 'carburanttrack-horsligne',
  ) {
    this.base = openDB<SchemaHorsLigne>(nomBase, 2, {
      upgrade(db, ancienne) {
        if (ancienne < 1) {
          const store = db.createObjectStore('operations', { keyPath: 'identifiantClient' });
          store.createIndex('etat', 'etat');
        }
        if (ancienne < 2) db.createObjectStore('donnees', { keyPath: 'cle' });
      },
    });
  }

  async ajouter(type: string, charge: unknown, options: Date | OptionsAjout = {}): Promise<OperationStockee> {
    const o: OptionsAjout = options instanceof Date ? { horodatageEvenement: options } : options;
    const operation: OperationStockee = {
      identifiantClient: globalThis.crypto.randomUUID(),
      type,
      charge: await this.coffre.chiffrer(charge),
      horodatageEvenement: (o.horodatageEvenement ?? new Date()).toISOString(),
      horodatageLocal: new Date().toISOString(),
      etat: 'enregistre_local',
      motif: null,
      versionObjet: o.versionObjet ?? null,
      resume: o.resume ? await this.coffre.chiffrer(o.resume) : null,
    };
    await (await this.base).put('operations', operation);
    await this.memoriserEmpreinte();
    return operation;
  }

  async lister(): Promise<OperationStockee[]> {
    const toutes = await (await this.base).getAll('operations');
    return toutes.sort((a, b) => a.horodatageLocal.localeCompare(b.horodatageLocal));
  }

  async lire(identifiantClient: string): Promise<OperationStockee | undefined> {
    return (await this.base).get('operations', identifiantClient);
  }

  async charge<T>(identifiantClient: string): Promise<T> {
    const op = await (await this.base).get('operations', identifiantClient);
    if (!op) throw new Error('Opération inconnue');
    return this.coffre.dechiffrer<T>(op.charge);
  }

  async resume(op: OperationStockee): Promise<string | null> {
    if (!op.resume) return null;
    try {
      return await this.coffre.dechiffrer<string>(op.resume);
    } catch {
      return null;
    }
  }

  async changerEtat(identifiantClient: string, etat: EtatOperation, motif: string | null = null, complement: Partial<OperationStockee> = {}): Promise<void> {
    const db = await this.base;
    const op = await db.get('operations', identifiantClient);
    if (!op) throw new Error('Opération inconnue');
    // Rien n'est présenté comme reçu par le serveur avant l'accusé : « synchronisé » exige un envoi préalable.
    if (etat === 'synchronise' && op.etat !== 'envoye') throw new Error('Synchronisation sans accusé du serveur');
    await db.put('operations', { ...op, ...complement, etat, motif });
  }

  /** Retire une opération (purge des refus après 30 jours, ou opération remplacée par une résolution). */
  async retirer(identifiantClient: string): Promise<void> {
    await (await this.base).delete('operations', identifiantClient);
    await this.memoriserEmpreinte();
  }

  async ecrireDonnee(cle: string, contenu: unknown, version: string | null): Promise<void> {
    await (await this.base).put('donnees', { cle, version, enregistreLe: new Date().toISOString(), contenu: await this.coffre.chiffrer(contenu) });
  }

  async lireDonnee<T>(cle: string): Promise<{ version: string | null; enregistreLe: string; contenu: T } | null> {
    const d = await (await this.base).get('donnees', cle);
    if (!d) return null;
    try {
      return { version: d.version, enregistreLe: d.enregistreLe, contenu: await this.coffre.dechiffrer<T>(d.contenu) };
    } catch {
      // Clé de session perdue : la donnée locale est illisible, elle sera rechargée depuis le serveur.
      return null;
    }
  }

  async effacerDonnees(): Promise<void> {
    await (await this.base).clear('donnees');
  }

  async fermer(): Promise<void> {
    (await this.base).close();
  }

  private async calculerEmpreinte(ids: string[]): Promise<string> {
    const octets = new TextEncoder().encode([...ids].sort().join('|'));
    const h = await globalThis.crypto.subtle.digest('SHA-256', octets as BufferSource);
    return Array.from(new Uint8Array(h), (o) => o.toString(16).padStart(2, '0')).join('');
  }

  private async memoriserEmpreinte(): Promise<void> {
    const ids = (await this.lister()).map((o) => o.identifiantClient);
    this.empreintes.setItem(CLE_EMPREINTE, JSON.stringify({ empreinte: await this.calculerEmpreinte(ids), ids }));
  }

  /** Accepte l'état courant comme référence (après avertissement d'une purge, l'utilisateur en a pris acte). */
  async prendreActeDeLaPurge(): Promise<void> {
    await this.memoriserEmpreinte();
  }

  /** Détecte une purge du stockage : la file ne correspond plus à l'empreinte conservée séparément. */
  async verifierIntegrite(): Promise<{ intacte: boolean; perdus: string[] }> {
    const brut = this.empreintes.getItem(CLE_EMPREINTE);
    if (!brut) return { intacte: true, perdus: [] };
    const attendu = JSON.parse(brut) as { empreinte: string; ids: string[] };
    const presents = (await this.lister()).map((o) => o.identifiantClient);
    if ((await this.calculerEmpreinte(presents)) === attendu.empreinte) return { intacte: true, perdus: [] };
    return { intacte: false, perdus: attendu.ids.filter((id) => !presents.includes(id)) };
  }
}
